/**
 * Works for both FindOffice and AgencyVerification forms.
 * It binds to forms matching `formSelector` (default: 'form.find-office-form')
 * and builds the API URL using per-form-type param mappers.
 */

export function initListingsSubmit({
  formSelector = "form.find-office-form",
} = {}) {
  const forms = document.querySelectorAll(formSelector);
  if (!forms.length) return;

  const LISTINGS_DEFAULT = "/api/ESB/GetOfficeListing";

  /** Track AbortControllers per form to cancel in-flight requests independently */
  const inflightByForm = new WeakMap();

  forms.forEach(form => {
    if (form.dataset.foInit === "true") return;
    form.dataset.foInit = "true";

    form.addEventListener("submit", async e => {
      e.preventDefault();

      const formType = normalizeFormType(form.dataset.formType);
      const params = collectParams(form, formType);

      if (formType === "agencyVerification") {
        const agencyInput = form.querySelector('input[name="agencyname"]');
        const cityInput = form.querySelector('input[name="city"]');
        const hasOne = !!(params.agencyName || params.city);

        if (!hasOne) {
          [agencyInput, cityInput].forEach(
            el => el && el.setAttribute("aria-invalid", "true"),
          );
          const msg = "Please enter a city or agency name";
          showError(form, msg);
          emitBoth(form, "error", { message: msg, kind: "validation" });
          return;
        } else {
          [agencyInput, cityInput].forEach(
            el => el && el.removeAttribute("aria-invalid"),
          );
          showError(form, "");
        }
      }

      const base = form.dataset.listingsUrl || LISTINGS_DEFAULT;
      const url = buildListingsUrl(base, params, form.dataset, formType);

      const submitBtn = form.querySelector(
        'button[type="submit"], input[type="submit"]',
      );

      setBusy(form, submitBtn, true);

      // Abort previous request for this form (if any)
      const prev = inflightByForm.get(form);
      if (prev) prev.abort();
      const ac = new AbortController();
      inflightByForm.set(form, ac);

      try {
        emitBoth(form, "loading", { loading: true });

        const res = await fetch(url.toString(), {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: ac.signal,
        });
        if (res.status === 404) {
          emitBoth(form, "results", {
            url: url.toString(),
            data: null,
            list: [],
            status: 404,
          });
        } else if (!res.ok) {
          const err = new Error(
            `HTTP ${res.status} ${res.statusText || ""}`.trim(),
          );
          err.status = res.status;
          throw err;
        } else {
          const data = await res.json();
          let list = getFirstArray(data);

          // Filter by serviceState if provided
          if (formType === "agencyVerification" && params.serviceState) {
            list = filterByServiceState(list, params.serviceState);
          }

          emitBoth(form, "results", {
            url: url.toString(),
            data,
            list,
            status: res.status,
          });
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          showError(form, "Could not complete the search. Please try again.");
          emitBoth(form, "error", {
            message: err.message || String(err),
            status: err.status,
            kind: "request",
          });
          console.error(err);
        }
      } finally {
        setBusy(form, submitBtn, false);
        emitBoth(form, "loading", { loading: false });
      }
    });
  });

  // -------- Utils --------

  function normalizeFormType(v) {
    const val = (v || "").toLowerCase().trim();
    if (val === "agencyverification") return "agencyVerification";
    return "findOffice"; // default
  }

  /** Read trimmed values from the form controls */
  function getVal(root, selector) {
    const el = root.querySelector(selector);
    return el ? (el.value || "").trim() : "";
  }

  /**
   * Map DOM fields -> logical params depending on the form type.
   * - findOffice: address, city, state, distance
   * - agencyVerification: agencyName, city, state, zip, serviceState
   */
  function collectParams(form, formType) {
    if (formType === "agencyVerification") {
      const agencyName = getVal(form, 'input[name="agencyname"]');
      const city = getVal(form, 'input[name="city"]');
      const state = getVal(form, 'select[name="state-select"]');
      const zip = getVal(form, 'input[name="zip"]');
      const serviceState = getVal(form, 'select[name="serviceState"]');
      return { agencyName, city, state, zip, serviceState };
    }

    // default: findOffice
    const address = getVal(form, 'input[name="address"]');
    const city = getVal(form, 'input[name="city"]');
    const state = getVal(form, 'select[name="state-select"]');
    const distance = getVal(form, 'select[name="distance-select"]');
    const marketArea = getVal(form, 'input[name="marketArea"]'); // <-- Add this line
    return { address, city, state, distance, marketArea }; // <-- Add marketArea
  }

  /**
   * Build the API URL from base + params + dataset defaults.
   * Dataset attrs remain the single source of truth for these settings.
   * Only set a query param if a value is present (avoid empty filters).
   */
  function buildListingsUrl(base, params, ds, formType = "findOffice") {
    const url = isAbsoluteUrl(base)
      ? new URL(base)
      : new URL(base, window.location.origin);
    const qs = url.searchParams;

    if (formType === "findOffice") {
      qs.set("office_visibility", ds.officeVisibility || "[both]");
    }

    qs.set("agent_type", ds.agentTypes || "I,E1,E2,S1,S2");
    qs.set("limit", ds.limit || "1000");

    // Radius logic
    const shouldRadius = "true";
    qs.set("isRadiusSearch", String(shouldRadius));

    if (ds.defaultDistance && !params.distance) {
      qs.set("distance", ds.defaultDistance);
    }

    if (formType === "findOffice") {
      if (params.address) qs.set("address", params.address || "");
      if (params.city) qs.set("city", params.city);
      if (params.state) qs.set("state", params.state);
      if (params.distance && params.distance !== "undefined") {
        qs.set("distance", params.distance);
      }
      if (params.marketArea) qs.set("marketarea", params.marketArea);
    } else if (formType === "agencyVerification") {
      if (params.agencyName) qs.set("agencyname", params.agencyName);
      if (params.city) qs.set("city", params.city);
      if (params.state) qs.set("state", params.state);
      if (params.serviceState) qs.set("serviceState", params.serviceState);
      if (params.zip) qs.set("zip", params.zip);
      qs.set("isRadiusSearch", "false");
    }

    return url;
  }

  function emitBoth(form, stage, detail) {
    const map = {
      loading: { newEvt: "listings:loading", oldEvt: "findoffice:loading" },
      results: { newEvt: "listings:results", oldEvt: "findoffice:results" },
      error: { newEvt: "listings:error", oldEvt: "findoffice:error" },
    };
    const pair = map[stage];
    if (!pair) return;

    emit(form, pair.newEvt, detail);
    emit(form, pair.oldEvt, detail);

    if (stage === "loading") {
      const isOn = !!(detail && detail.loading);
      form.setAttribute("aria-busy", isOn ? "true" : "false");
    } else {
      form.setAttribute("aria-busy", "false");
    }
  }

  /** Dispatch helper to keep event shape consistent */
  function emit(form, type, detail) {
    form.dispatchEvent(
      new CustomEvent(type, { bubbles: true, detail: detail || {} }),
    );
  }

  /** Toggle busy/idle UI and update submit button label */
  function setBusy(form, btn, on) {
    if (!btn) return;
    const isInput = btn.tagName === "INPUT";

    if (on) {
      btn.disabled = true;
      if (isInput) {
        btn.dataset.label = btn.dataset.label || btn.value;
        btn.value = "Searching...";
      } else {
        btn.dataset.label = btn.dataset.label || btn.textContent;
        btn.textContent = "Searching...";
      }
    } else {
      btn.disabled = false;
      if (isInput && btn.dataset.label) btn.value = btn.dataset.label;
      else if (!isInput && btn.dataset.label)
        btn.textContent = btn.dataset.label;
      delete btn.dataset.label;
    }
  }

  /** Show a generic error message near the form */
  function showError(form, msg) {
    const err = form.querySelector(
      ".js-general-error, .general-error, .sc-form__error",
    );
    if (err) err.textContent = msg;
  }
}

export function initFindOfficeSubmit() {
  return initListingsSubmit({ formSelector: "form.find-office-form" });
}

/** Filter results by service state */
function filterByServiceState(list, serviceState) {
  if (!list || !Array.isArray(list) || !serviceState) return list;
  return list.filter(item => {
    if (item.ServiceState) {
      return item.ServiceState.toLowerCase() === serviceState.toLowerCase();
    }
    // If item has no service state info, exclude from filtered results
    return false;
  });
}

/** Recursively find the first array in an object */
function getFirstArray(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  for (const v of Object.values(data)) {
    if (Array.isArray(v)) return v;
    if (v && typeof v === "object") {
      const nested = getFirstArray(v);
      if (Array.isArray(nested) && nested.length) return nested;
    }
  }
  return [];
}

function isAbsoluteUrl(str) {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}
